import React from "react";
import { cn } from "../../lib/utils";
import { Icons } from "./icons";

interface FormStepperProps {
  steps: {
    id: string;
    label: string;
  }[];
  currentStep: string;
  onStepClick: (stepId: string) => void;
  completedSteps: string[];
  orientation?: "vertical" | "horizontal";
  className?: string;
}

export function FormStepper({
  steps,
  currentStep,
  onStepClick,
  completedSteps,
  orientation = "vertical",
  className,
}: FormStepperProps) {
  if (orientation === "horizontal") {
    return (
      <nav className={cn("min-w-0", className)} aria-label="Steps">
        <ol className="flex min-w-0 items-center gap-2">
          {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = completedSteps.includes(step.id);

            return (
              <li
                key={step.id}
                className="flex min-w-0 flex-1 items-center gap-2 last:flex-none"
              >
                <button
                  type="button"
                  onClick={() => onStepClick(step.id)}
                  aria-current={isActive ? "step" : undefined}
                  className={cn(
                    "flex min-w-0 items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                    isActive && "bg-gray-900 text-white",
                    !isActive &&
                      isCompleted &&
                      "bg-gray-100 text-gray-900 hover:bg-gray-200",
                    !isActive &&
                      !isCompleted &&
                      "text-gray-500 hover:bg-gray-100",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
                      isActive && "bg-white text-gray-900",
                      !isActive && isCompleted && "bg-gray-900 text-white",
                      !isActive && !isCompleted && "bg-gray-200 text-gray-600",
                    )}
                  >
                    {isCompleted && !isActive ? (
                      <Icons.Tick className="h-3 w-3 text-white" />
                    ) : (
                      index + 1
                    )}
                  </span>
                  <span className="truncate">{step.label}</span>
                </button>
                {index < steps.length - 1 && (
                  <span
                    className="h-px min-w-4 flex-1 bg-gray-200"
                    aria-hidden
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {steps.map((step) => {
        const isActive = step.id === currentStep;
        const isCompleted = completedSteps.includes(step.id);

        return (
          <button
            key={step.id}
            type="button"
            onClick={() => onStepClick(step.id)}
            className={cn(
              "flex h-9 items-center justify-between px-3 rounded-lg text-sm font-medium transition-colors",
              isActive && "bg-gray-900 text-white",
              !isActive && "hover:bg-gray-200 text-gray-700",
            )}
          >
            <span>{step.label}</span>
            {isCompleted && <Icons.Tick className="h-4 w-4 text-gray-500" />}
          </button>
        );
      })}
    </div>
  );
}
